import React from "react";
import { generatedBetaClosureStatus } from "../data/generatedBetaClosureStatus.js";
import { generatedBetaReviewDocket } from "../data/generatedBetaReviewDocket.js";
import { generatedPromotionClosureLedger } from "../data/generatedPromotionClosureLedger.js";
import { generatedReleaseEvidenceLedger } from "../data/generatedReleaseEvidenceLedger.js";
import { generatedUiFidelityApprovalPacket } from "../data/generatedUiFidelityApprovalPacket.js";
import { generatedUiFidelityLedger } from "../data/generatedUiFidelityLedger.js";
import { CellStack, DataTable, EvidencePanel, Metric, PageHeader, StatusPill } from "../components/ui.jsx";

export function ApprovalBoard({ approvalLog }) {
  const gateRows = generatedReleaseEvidenceLedger.gates;
  return (
    <section className="module">
      <PageHeader
        title="APPROVAL BOARD"
        muted="Review state"
        subtitle="Release gatebook with local verification, science evidence, human approvals, and external proof blockers."
      />
      <div className="metric-grid-4">
        <Metric label="Release allowed" value={generatedReleaseEvidenceLedger.releaseAllowed ? "yes" : "no"} />
        <Metric label="Gate count" value={generatedReleaseEvidenceLedger.gateCount} />
        <Metric label="Blocked gates" value={generatedReleaseEvidenceLedger.blockedGateCount} />
        <Metric label="Ledger hash" value={generatedReleaseEvidenceLedger.ledgerHash.slice(0, 18)} />
        <Metric label="Closure plans" value={generatedPromotionClosureLedger.planCount} />
        <Metric label="Closure blocked" value={generatedPromotionClosureLedger.blockedClosurePlanCount} />
        <Metric label="Closure partial" value={generatedPromotionClosureLedger.partialClosurePlanCount} />
        <Metric label="Closure hash" value={generatedPromotionClosureLedger.ledgerHash.slice(0, 18)} />
        <Metric label="Review slots" value={generatedBetaReviewDocket.approvalSlotCount} />
        <Metric label="Slots blocked" value={generatedBetaReviewDocket.blockedSlotCount} />
        <Metric label="Docket hash" value={generatedBetaReviewDocket.docketHash.slice(0, 18)} />
        <Metric label="Release signoff" value={generatedBetaReviewDocket.releaseApprovalAllowed ? "allowed" : "blocked"} />
        <Metric label="Closure status" value={generatedBetaClosureStatus.statusHash.slice(0, 18)} />
        <Metric label="Closure blockers" value={generatedBetaClosureStatus.blockerCount} />
        <Metric label="Public beta" value={generatedBetaClosureStatus.currentUseState.publicBetaAllowed ? "allowed" : "blocked"} />
        <Metric label="Monitor proof" value={generatedBetaClosureStatus.monitorProofContract.requiredChecks.length} />
        <Metric label="UI refs" value={generatedUiFidelityLedger.referenceImageCount} />
        <Metric label="UI captures" value={generatedUiFidelityLedger.renderedCaptureCount} />
        <Metric label="UI pages blocked" value={generatedUiFidelityLedger.blockedPageCount} />
        <Metric label="UI ledger" value={generatedUiFidelityLedger.ledgerHash.slice(0, 18)} />
      </div>
      <EvidencePanel title="Beta closure status" status={generatedBetaClosureStatus.mode} className="panel-large">
        <p>{generatedBetaClosureStatus.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Lowest readiness" value={generatedBetaClosureStatus.readinessSummary.lowestCurrentScore} />
          <Metric label="Blocked lanes" value={Object.keys(generatedBetaClosureStatus.blockerLaneCounts).length} />
          <Metric label="Customer delivery" value={generatedBetaClosureStatus.currentUseState.customerDeliveryAllowed ? "allowed" : "blocked"} />
          <Metric label="Commercial use" value={generatedBetaClosureStatus.currentUseState.commercialUseAllowed ? "allowed" : "blocked"} />
        </div>
        <DataTable
          rows={generatedBetaClosureStatus.blockerRows.slice(0, 12)}
          getRowKey={(row) => row.id}
          columns={[
            { key: "lane", header: "Lane" },
            {
              key: "status",
              header: "Status",
              render: (row) => (
                <StatusPill tone={row.status === "pass" ? "green" : row.status === "partial" ? "blue" : "amber"}>
                  {row.status}
                </StatusPill>
              ),
            },
            { key: "owner", header: "Owner" },
            { key: "requiredProof", header: "Required proof" },
            {
              key: "nextAction",
              header: "Next action",
              render: (row) => row.nextActions?.[0] ?? "n/a",
            },
            {
              key: "rowHash",
              header: "Hash",
              render: (row) => <code>{row.rowHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Beta review docket" status={generatedBetaReviewDocket.mode} className="panel-large">
        <p>{generatedBetaReviewDocket.operatingRule}</p>
        <DataTable
          rows={generatedBetaReviewDocket.approvalSlots}
          getRowKey={(slot) => slot.id}
          columns={[
            { key: "label", header: "Signoff slot" },
            {
              key: "state",
              header: "State",
              render: (slot) => <StatusPill tone={slot.approvalAllowed ? "green" : "amber"}>{slot.state}</StatusPill>,
            },
            { key: "owner", header: "Owner" },
            {
              key: "approval",
              header: "Approval",
              render: (slot) => (slot.approvalAllowed ? "allowed" : "blocked"),
            },
            { key: "requiredProof", header: "Required proof" },
            { key: "nextAction", header: "Next action" },
            {
              key: "slotHash",
              header: "Hash",
              render: (slot) => <code>{slot.slotHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="UI fidelity ledger" status={generatedUiFidelityLedger.mode} className="panel-large">
        <p>{generatedUiFidelityLedger.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Required pages" value={generatedUiFidelityLedger.requiredPageCount} />
          <Metric label="References" value={generatedUiFidelityLedger.referenceImageCount} />
          <Metric label="Rendered captures" value={generatedUiFidelityLedger.renderedCaptureCount} />
          <Metric label="Review packet" value={generatedUiFidelityApprovalPacket.packetHash.slice(0, 18)} />
          <Metric label="Owner approval" value={generatedUiFidelityLedger.ownerApprovalAllowed ? "allowed" : "blocked"} />
        </div>
        <div className="actions-row">
          <a className="button-secondary" href="/ui-fidelity-review/" target="_blank" rel="noreferrer">
            Open side-by-side packet
          </a>
          <a className="button-secondary" href="/ui-fidelity-review-packet.json" download>
            Download packet JSON
          </a>
        </div>
        <DataTable
          rows={generatedUiFidelityLedger.pageRecords}
          getRowKey={(record) => record.id}
          columns={[
            { key: "label", header: "Page" },
            {
              key: "fidelityState",
              header: "State",
              render: (record) => (
                <StatusPill tone={record.ownerApprovalAllowed ? "green" : "amber"}>{record.fidelityState}</StatusPill>
              ),
            },
            {
              key: "reference",
              header: "Reference",
              render: (record) => <code>{record.referenceImage.sha256?.slice(0, 18) ?? "missing"}</code>,
            },
            {
              key: "rendered",
              header: "Rendered",
              render: (record) => <code>{record.renderedImage.sha256?.slice(0, 18) ?? "missing"}</code>,
            },
            {
              key: "aspectDelta",
              header: "Aspect delta",
              render: (record) => record.comparison.aspectRatioDelta ?? "n/a",
            },
            { key: "nextAction", header: "Next action" },
            {
              key: "pageHash",
              header: "Hash",
              render: (record) => <code>{record.pageHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Release evidence ledger" status={generatedReleaseEvidenceLedger.mode} className="panel-large">
        <p>{generatedReleaseEvidenceLedger.operatingRule}</p>
        <DataTable
          rows={gateRows}
          getRowKey={(gate) => gate.id}
          columns={[
            { key: "label", header: "Gate" },
            {
              key: "status",
              header: "Status",
              render: (gate) => (
                <StatusPill tone={gate.status === "pass" ? "green" : gate.status === "partial" ? "blue" : "amber"}>
                  {gate.status}
                </StatusPill>
              ),
            },
            { key: "owner", header: "Owner" },
            { key: "requiredProof", header: "Required proof" },
            { key: "nextAction", header: "Next action" },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Promotion closure proof board"
        status={generatedPromotionClosureLedger.promotionAllowed ? "claim ready" : "claim blocked"}
        tone={generatedPromotionClosureLedger.promotionAllowed ? "green" : "red"}
        className="panel-large"
      >
        <p>{generatedPromotionClosureLedger.operatingRule}</p>
        <DataTable
          rows={generatedPromotionClosureLedger.closurePlans}
          getRowKey={(plan) => plan.releaseGateId}
          columns={[
            {
              key: "title",
              header: "Closure plan",
              render: (plan) => <CellStack title={plan.title} meta={plan.releaseGateId} />,
            },
            { key: "owner", header: "Owner" },
            {
              key: "closureState",
              header: "State",
              render: (plan) => (
                <StatusPill
                  tone={plan.closureState === "closed" ? "green" : plan.closureState === "partial-proof" ? "blue" : "amber"}
                >
                  {plan.closureState}
                </StatusPill>
              ),
            },
            {
              key: "proofPackages",
              header: "Proof packages",
              render: (plan) =>
                `${plan.requiredProofPackages.sourcePayloadWorkOrders.length} source / ${plan.requiredProofPackages.linkedEvidenceGaps.length} gaps`,
            },
            {
              key: "runtimeGaps",
              header: "Runtime gaps",
              render: (plan) => plan.unresolvedCounts.runtimeContracts,
            },
            {
              key: "nextSprintActions",
              header: "Next sprint actions",
              render: (plan) => plan.nextSprintActions.slice(0, 2).join(" | "),
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Review log" status={`${approvalLog.length} entries`} tone="blue">
        {approvalLog.length === 0 ? (
          <p>No actions yet.</p>
        ) : (
          <DataTable
            rows={approvalLog}
            getRowKey={(entry) => entry.id}
            columns={[
              { key: "at", header: "Time" },
              { key: "target", header: "Target" },
              { key: "action", header: "Action" },
              {
                key: "entryHash",
                header: "Hash",
                render: (entry) => <code>{entry.entryHash?.slice(0, 18) ?? "legacy"}</code>,
              },
            ]}
          />
        )}
      </EvidencePanel>
    </section>
  );
}
